/* ==========================================================================
   PRODUCT PAGE

   Gallery, size picker, quantity, add to bag, save, and share.

   The add posts straight to /cart/add.js and then announces the theme's own
   cart:change event, which is what the cart panel listens for -- so nothing
   here depends on Prestige's product form.
   ========================================================================== */

(function () {
  'use strict';

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const money = (cents) => {
    const fmt = window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  /* ---------- Breadcrumb ---------- */

  /* Liquid only knows the collection when the URL carries it, and our links go
     straight to /products/... So when the crumb is empty, work it out from the
     referrer: if the shopper came from one of this product's own collections,
     that is the one to show. */
  (function fillCollectionCrumb() {
    const link = document.querySelector('[data-syn-crumb-collection]');
    const sep = document.querySelector('[data-syn-crumb-sep]');
    if (!link || !link.hidden) return;                 // already filled by Liquid

    const tag = document.querySelector('[data-syn-collections]');
    if (!tag || !document.referrer) return;

    let map;
    try { map = JSON.parse(tag.textContent); } catch (e) { return; }

    let referrer;
    try { referrer = new URL(document.referrer); } catch (e) { return; }
    if (referrer.host !== window.location.host) return;

    const match = referrer.pathname.match(/\/collections\/([^/?#]+)/);
    if (!match) return;

    const entry = map[match[1]];
    if (!entry) return;                                // not one of this product's

    link.textContent = entry.title;
    link.href = entry.url;
    link.hidden = false;
    if (sep) sep.hidden = false;
  })();

  /* ---------- Gallery ---------- */

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-syn-thumb]');
    if (!thumb) return;
    const index = thumb.getAttribute('data-syn-thumb');
    const gallery = thumb.closest('.syn-pdp__gallery');
    if (!gallery) return;

    gallery.querySelectorAll('[data-syn-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
    gallery.querySelectorAll('[data-syn-slide]').forEach((s) => {
      s.classList.toggle('is-active', s.getAttribute('data-syn-slide') === index);
    });
  });

  /* ---------- Zoom ---------- */

  /* Clicking the hero opens it full screen. The sources are rendered with the
     page at a larger width, so opening costs one image request at most. */
  (function zoom() {
    const overlay = document.querySelector('[data-syn-zoom]');
    const image = document.querySelector('[data-syn-zoom-image]');
    const tag = document.querySelector('[data-syn-zoom-sources]');
    if (!overlay || !image || !tag) return;

    let sources = [];
    try { sources = JSON.parse(tag.textContent); } catch (e) { return; }
    if (!sources.length) return;

    let index = 0;

    const show = (i) => {
      index = (i + sources.length) % sources.length;
      image.src = sources[index];
    };

    const open = (i) => {
      show(i);
      overlay.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
    };

    const close = () => {
      overlay.hidden = true;
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };

    function onKey(event) {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') show(index + 1);
      if (event.key === 'ArrowLeft') show(index - 1);
    }

    document.addEventListener('click', (event) => {
      const stage = event.target.closest('.syn-pdp__stage');
      if (stage) {
        const active = document.querySelector('.syn-pdp__slide.is-active');
        open(active ? Number(active.getAttribute('data-syn-slide')) || 0 : 0);
        return;
      }

      const step = event.target.closest('[data-syn-zoom-step]');
      if (step) {
        // Stop the click reaching the overlay, which would close it.
        event.stopPropagation();
        show(index + Number(step.getAttribute('data-syn-zoom-step')));
        return;
      }

      if (event.target.closest('[data-syn-zoom-close]')) { close(); return; }
      if (!overlay.hidden && event.target.closest('[data-syn-zoom]')) close();
    });
  })();

  /* ---------- Variants ---------- */

  const form = document.querySelector('[data-syn-product-form]');
  const dataTag = document.querySelector('[data-syn-variants]');
  const variants = dataTag ? JSON.parse(dataTag.textContent) : [];

  /* The first available size is preselected, as Shopify does by default, so
     add-to-bag and express checkout work without a step in between. Saving to
     the wish list does not ask either -- the wish list panel has its own size
     picker, which is where that choice gets made. */
  const optionInputs = () => Array.from(document.querySelectorAll('[data-syn-option]'));

  function chosenOptions() {
    const groups = {};
    optionInputs().forEach((input) => {
      if (input.checked) groups[input.name] = input.value;
    });
    return groups;
  }

  function allOptionsChosen() {
    const names = new Set(optionInputs().map((i) => i.name));
    if (!names.size) return true;                    // nothing to choose
    const chosen = chosenOptions();
    return [...names].every((n) => chosen[n]);
  }

  function currentVariant() {
    if (variants.length === 1) return variants[0];
    if (!allOptionsChosen()) return null;
    const chosen = Object.keys(chosenOptions())
      .sort()
      .map((k) => chosenOptions()[k]);
    return variants.find((v) => {
      const vals = [v.option1, v.option2, v.option3].filter((x) => x != null);
      return vals.length === chosen.length && vals.every((val, i) => val === chosen[i]);
    }) || null;
  }

  function note(message) {
    const el = document.querySelector('[data-syn-form-note]');
    if (!el) return;
    if (!message) { el.hidden = true; el.textContent = ''; return; }
    el.textContent = message;
    el.hidden = false;
  }

  /* The gallery belongs to the selected size. A merged product holds every
     size's photographs, so without this a shopper looking at 3.3 oz also sees
     the 1.7 oz bottle sitting in the thumbnails.

     The map comes from a product metafield because Liquid can only expose a
     variant's single featured image, not its whole set. No map means an
     unmerged product, and everything stays visible. */
  const mediaMapTag = document.querySelector('[data-syn-variant-media]');
  let mediaMap = null;
  try {
    const parsed = mediaMapTag ? JSON.parse(mediaMapTag.textContent) : null;
    if (parsed && Object.keys(parsed).length) mediaMap = parsed;
  } catch (e) { /* leave every image showing */ }

  const mediaNumber = (gid) => String(gid).split('/').pop();

  function showVariantImage(variant) {
    if (!variant) return;

    const slides = Array.from(document.querySelectorAll('[data-syn-slide]'));
    const thumbs = Array.from(document.querySelectorAll('[data-syn-thumb]'));
    if (!slides.length) return;

    const owned = mediaMap
      ? (mediaMap['gid://shopify/ProductVariant/' + variant.id] || mediaMap[variant.id] || []).map(mediaNumber)
      : null;

    const featured = variant.featured_media ? String(variant.featured_media.id) : null;
    const belongs = (el) => {
      const id = el.getAttribute('data-media-id');
      if (!owned || !owned.length) return true;
      return owned.includes(id);
    };

    // Hide the sizes that are not selected, then show that size's own shot.
    slides.forEach((s) => { s.hidden = !belongs(s); });
    thumbs.forEach((t) => { t.hidden = !belongs(t); });

    /* Order the thumbnails by the pair, not by the product's media order. The
       product interleaves the sizes -- 3.3 styled, 1.7 packshot, 1.7 styled,
       3.3 packshot -- so filtering alone can leave the packshot first. The map
       lists the styled shot first, and flex order puts them that way without
       moving anything in the DOM. */
    if (owned && owned.length) {
      thumbs.forEach((t) => {
        const at = owned.indexOf(t.getAttribute('data-media-id'));
        t.style.order = at === -1 ? '99' : String(at);
      });
    }

    const visible = slides.filter((s) => !s.hidden);
    const target = visible.find((s) => s.getAttribute('data-media-id') === featured) || visible[0];
    slides.forEach((s) => s.classList.toggle('is-active', s === target));
    thumbs.forEach((t) => {
      t.classList.toggle('is-active', !t.hidden && t.getAttribute('data-media-id') === target?.getAttribute('data-media-id'));
    });
  }

  /* Swap the copy with the size, the same way the gallery swaps. Falls back to
     whatever is already on the page when a variant has no copy of its own. */
  const stockTag = document.querySelector('[data-syn-variant-stock]');
  let stockMap = null;
  try {
    const parsed = stockTag ? JSON.parse(stockTag.textContent) : null;
    if (parsed && Object.keys(parsed).length) stockMap = parsed;
  } catch (e) { /* no cap; the server still enforces it */ }

  /* Size and concentration for every variant, so the two lines under the title
     follow the chosen size like the description does. */
  const factsTag = document.querySelector('[data-syn-variant-facts]');
  let variantFacts = {};
  try { variantFacts = factsTag ? JSON.parse(factsTag.textContent) : {}; } catch (e) { variantFacts = {}; }

  function showVariantFacts(variant) {
    const facts = variant ? variantFacts[variant.id] : null;
    for (const [sel, key] of [['[data-syn-size]', 'size'], ['[data-syn-type]', 'type']]) {
      const el = document.querySelector(sel);
      if (!el) continue;
      const value = facts ? (facts[key] || '') : '';
      el.textContent = value;
      el.hidden = !value;
    }
  }

  const copyTag = document.querySelector('[data-syn-variant-copy]');
  let copyMap = null;
  try {
    const parsed = copyTag ? JSON.parse(copyTag.textContent) : null;
    if (parsed && Object.keys(parsed).length) copyMap = parsed;
  } catch (e) { /* leave the description as rendered */ }

  function showVariantCopy(variant) {
    if (!copyMap || !variant) return;
    const copy = copyMap[String(variant.id)];
    if (typeof copy !== 'string' || !copy.trim()) return;

    const el = document.querySelector('.syn-pdp__description');
    if (el) el.innerHTML = copy;

    /* Share what is on screen. The share element is rendered once with the
       product's description, so without this a shopper reading the 1.7 oz page
       would post the 3.3 oz copy -- and a link to a size they did not pick. */
    const share = document.querySelector('synergy-share');
    if (!share) return;

    const plain = copy.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    share.setAttribute('text', plain.slice(0, 400));

    const url = new URL(share.getAttribute('url'), window.location.origin);
    url.searchParams.set('variant', variant.id);
    share.setAttribute('url', url.toString());
  }

  function paintVariant() {
    const variant = currentVariant();
    const input = document.querySelector('[data-syn-variant-input]');
    const priceEl = document.querySelector('[data-syn-price]');
    const compareEl = document.querySelector('[data-syn-compare]');
    const button = document.querySelector('[data-syn-add]');
    const label = document.querySelector('[data-syn-add-label]');

    if (input) input.value = variant ? variant.id : '';

    if (variant) {
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        const show = variant.compare_at_price && variant.compare_at_price > variant.price;
        compareEl.hidden = !show;
        if (show) compareEl.textContent = money(variant.compare_at_price);
        // The price turns red only while a compare-at is beside it, so switching
        // to a size that is not on sale takes the red away with it.
        if (priceEl) priceEl.classList.toggle('syn-pdp__price--sale', !!show);

        // The hero badge makes the same claim as the struck price, so it has to
        // come and go with it. A product can be discounted in one size only --
        // a badge left behind on an undiscounted size is a false claim, not
        // just a stale one.
        // A sold-out size shows the sold-out badge and no saving: a discount is
        // not a claim worth making about something that cannot be bought. One
        // size can be gone while another is in stock, so this has to move with
        // the selection exactly as the price does.
        const soldOutEl = document.querySelector('[data-syn-badge-soldout]');
        if (soldOutEl) soldOutEl.hidden = !!variant.available;

        const badgeEl = document.querySelector('[data-syn-badge]');
        if (badgeEl) {
          badgeEl.hidden = !show || !variant.available;
          if (show) {
            const saved = variant.compare_at_price - variant.price;
            badgeEl.textContent =
              badgeEl.getAttribute('data-mode') === 'percentage'
                ? 'Save ' + Math.round((saved * 100) / variant.compare_at_price) + '%'
                : 'Save ' + money(saved);
          }
        }
      }
      if (button) button.disabled = !variant.available;

      /* Cap the quantity field at what is actually in stock. Shopify enforces
         this server-side whatever we send -- every variant is tracked and set
         to stop selling at zero -- but a shopper should meet the limit here
         rather than after pressing the button. */
      const qty = document.querySelector('[data-syn-qty-input]');
      if (qty) {
        const stock = stockMap ? Number(stockMap[String(variant.id)]) : NaN;
        if (Number.isFinite(stock) && stock > 0) {
          qty.max = String(stock);
          if (Number(qty.value) > stock) qty.value = String(stock);
        } else {
          qty.removeAttribute('max');
        }
      }
      if (label && button) {
        label.textContent = variant.available
          ? button.getAttribute('data-label-add') || label.textContent
          : button.getAttribute('data-label-soldout') || 'Sold out';
      }
      showVariantImage(variant);
      showVariantCopy(variant);
      showVariantFacts(variant);

      // Keep the address bar in step, so a shared or reloaded URL keeps the size.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    note('');
  }

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-syn-option]')) return;
    paintVariant();
  });

  /* ---------- Quantity ---------- */

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-syn-qty]');
    if (!step) return;
    const input = document.querySelector('[data-syn-qty-input]');
    if (!input) return;
    const next = Math.max(1, (Number(input.value) || 1) + Number(step.getAttribute('data-syn-qty')));
    input.value = String(next);
    capQuantity(input);
  });

  /* Tell them while they type, not after they press the button. `max` on a
     number input only binds the spinner arrows -- a typed 800 submits happily,
     and the shopper first learns the real number once 50 are already in the
     bag. */
  /* What is already in the bag counts against the stock. Without this the page
     offers the full 100 while the bag holds 50, and Shopify has to be the one
     to say no -- after the shopper has committed. */
  let inBag = {};

  async function readBag() {
    try {
      const cart = await (await fetch(root() + 'cart.js')).json();
      inBag = {};
      for (const line of cart.items || []) {
        inBag[String(line.id)] = (inBag[String(line.id)] || 0) + line.quantity;
      }
    } catch (e) { inBag = {}; }
  }

  function capQuantity(input) {
    const variant = currentVariant();
    if (!variant || !stockMap) return;
    const stock = Number(stockMap[String(variant.id)]);
    if (!Number.isFinite(stock) || stock <= 0) return;

    const held = Number(inBag[String(variant.id)] || 0);
    const room = Math.max(0, stock - held);

    if (room === 0) {
      input.value = '1';
      note(`All ${stock} are already in your bag.`);
      return;
    }

    const message = held
      ? `Only ${room} more available -- you already have ${held} in your bag.`
      : `Only ${stock} available.`;
    const wanted = Number(input.value);

    if (!Number.isFinite(wanted) || wanted <= room) {
      /* Clear only our own message. Typing fires input and then change, and the
         second pass sees the value we just corrected as valid -- so clearing
         unconditionally wiped the explanation the moment it appeared. */
      const el = document.querySelector('[data-syn-form-note]');
      if (el && el.textContent.trim() === message && wanted < room) note('');
      return;
    }

    input.value = String(room);
    note(message);
  }

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-syn-qty-input]')) capQuantity(event.target);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-syn-qty-input]')) capQuantity(event.target);
  });

  /* ---------- Add to bag ---------- */

  if (form) {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (!allOptionsChosen()) {
        note(form.getAttribute('data-choose-message') || 'Please select a size first');
        const first = document.querySelector('.syn-pdp__option');
        if (first) first.classList.add('is-missing');
        return;
      }

      const variant = currentVariant();
      if (!variant) { note('That combination is unavailable.'); return; }

      const button = form.querySelector('[data-syn-add]');
      const quantity = Number(document.querySelector('[data-syn-qty-input]')?.value || 1);
      if (button) button.disabled = true;

      try {
        const res = await fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity }] }),
        });

        const body = await res.json().catch(() => ({}));
        if (!res.ok) note(body.description || body.message || 'That could not be added to your bag.');

        /* Refresh regardless. Asking for more than is in stock adds what there
           is and reports the shortfall, so returning early on a non-OK response
           left the bag count reading zero while the bag held fifty. */
        const cart = await (await fetch(root() + 'cart.js')).json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true, detail: { baseEvent: 'variant:add', cart },
        }));
      } catch (e) {
        note('That could not be added to your bag.');
      } finally {
        if (button) button.disabled = false;
      }
    });
  }

  /* ---------- Share ---------- */

  if (!window.customElements.get('synergy-share')) {
    class SynergyShare extends HTMLElement {
      connectedCallback() {
        this._menu = this.querySelector('[data-syn-share-menu]');
        this._note = this.querySelector('[data-syn-share-note]');

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-syn-share-open]')) return this.toggle();
          if (event.target.closest('[data-syn-share-close]')) return this.close();

          const action = event.target.closest('[data-syn-share]');
          if (action) this.share(action.getAttribute('data-syn-share'), action);
        });

        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) this.close();
        });
      }

      get url() { return this.getAttribute('url') || window.location.href; }
      get title() { return this.getAttribute('title') || document.title; }
      get text() { return this.getAttribute('text') || ''; }

      toggle() { if (this._menu) (this._menu.hidden ? this.open() : this.close()); }
      open() { if (this._menu) this._menu.hidden = false; }
      close() {
        if (this._menu) this._menu.hidden = true;
        if (this._note) { this._note.hidden = true; this._note.textContent = ''; }
      }

      say(message) {
        if (!this._note) return;
        this._note.textContent = message;
        this._note.hidden = false;
      }

      async share(kind, button) {
        if (kind === 'email') {
          // A plain mailto: the product name as the subject, the description
          // and the link as the body.
          const subject = encodeURIComponent(this.title);
          const body = encodeURIComponent(`${this.text}\n\nLink: ${this.url}`);
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
          return;
        }

        if (kind === 'facebook') {
          window.open(
            'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(this.url),
            'share-facebook', 'width=640,height=600,noopener'
          );
          return;
        }

        if (kind === 'x') {
          /* X takes a prefilled post: the product name, then as much of the
             description as fits. A post is 280 characters and every link counts
             as 23 however long it really is, so the text gets 280 - 23 - 1 for
             the space. Anything longer is trimmed at a word so the post does not
             end mid-syllable.

             Instagram was dropped rather than fixed: it has no web composer to
             link to, so the button could only ever copy a link or open the OS
             share sheet, which is not what a share button should do. */
          const LIMIT = 280 - 23 - 1;
          let post = this.title;
          if (this.text) {
            const full = `${this.title}

${this.text}`;
            if (full.length <= LIMIT) {
              post = full;
            } else {
              const clipped = full.slice(0, LIMIT - 1);
              const lastSpace = clipped.lastIndexOf(' ');
              post = (lastSpace > this.title.length ? clipped.slice(0, lastSpace) : clipped).trimEnd() + '…';
            }
          }

          window.open(
            `https://x.com/intent/post?text=${encodeURIComponent(post)}&url=${encodeURIComponent(this.url)}`,
            'share-x', 'width=640,height=600,noopener'
          );
          return;
        }

        if (kind === 'copy') await this.copy(button, 'Copied to clipboard!');
      }

      async copy(button, message) {
        try {
          await navigator.clipboard.writeText(this.url);
        } catch (e) {
          // Clipboard access can be refused; a hidden field still copies.
          const field = document.createElement('textarea');
          field.value = this.url;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          try { document.execCommand('copy'); } catch (err) { /* nothing more to try */ }
          document.body.removeChild(field);
        }
        if (button) {
          const original = button.textContent;
          button.textContent = message;
          window.setTimeout(() => { button.textContent = original; }, 2400);
        } else {
          this.say(message);
        }
      }
    }
    window.customElements.define('synergy-share', SynergyShare);
  }

  /* ---------- Start ---------- */

  document.documentElement.addEventListener('cart:change', () => {
    readBag().then(() => {
      const input = document.querySelector('[data-syn-qty-input]');
      if (input) capQuantity(input);
    });
  });

  readBag();

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', paintVariant);
  else paintVariant();
})();
